import React, { useEffect, useMemo } from 'react'
import { connect } from 'react-redux'
import Styles from './UsersActivityCard.module.css'
import { Colors } from 'Theme'
import PropTypes from 'prop-types'
import { ChartComponent, IFSkeleton, IFText } from 'Components'
import AnalyticsSelectors from 'Stores/Analytics/Selectors'
import AnalyticsActions from 'Stores/Analytics/Actions'
import RequestState from 'Enums/RequestState'
import { formatNumber } from 'Utils/Calculations'
import { useTranslation } from 'react-i18next'
import InfinityEnums from 'Enums/InfinityEnums'

const UsersActivityCard = ({
  usersActivity,
  fetchUsersActivity,
  fetchUsersActivityRequestState,
  filter,
}) => {
  const { t } = useTranslation()

  useEffect(() => {
    fetchUsersActivity(filter)
  }, [])

  useEffect(() => {
    fetchUsersActivity(filter)
  }, [filter])

  const { series, options } = useMemo(() => {
    const data = usersActivity
    const periodDisplay = {
      lastYear: t('Analytics.LastYear'),
      thisYear: t('Analytics.ThisYear'),
      last6Months: t('Analytics.Last6Months'),
      last3Months: t('Analytics.Last3Months'),
      lastMonth: t('Analytics.LastMonth'),
      lastWeek: t('Analytics.LastWeek'),
    }

    const totalUsers = data?.periodData?.thisYear?.data?.totalUsers || 0

    const activeUsersPerPeriod = Object.keys(InfinityEnums.UsersActivity).map(
      (key) => {
        return data?.periodData?.[key]?.data?.activeUsers || 0
      },
    )
    const seriesData = activeUsersPerPeriod.map((active) =>
      totalUsers > 0 ? (active / totalUsers) * 100 : 0,
    )
    const labels = Object.keys(InfinityEnums.UsersActivityDurations).map(
      (key, idx) => {
        const count = activeUsersPerPeriod[idx] || 0
        return `${formatNumber(count)}`
      },
    )

    return {
      series: seriesData,

      options: {
        chart: {
          height: 390,
          type: 'radialBar',
        },
        plotOptions: {
          radialBar: {
            offsetY: 0,
            startAngle: 0,
            endAngle: 270,
            hollow: {
              margin: 5,
              size: '30%',
              background: 'transparent',
              image: undefined,
            },
            dataLabels: {
              name: {
                show: false,
              },
              value: {
                show: true,
                fontSize: '16px',
                fontWeight: 600,
                formatter: function (val) {
                  const rawValue = Math.round((val / 100) * totalUsers)
                  return `${formatNumber(rawValue)} user`
                },
              },
              total: {
                show: true,
                label: t('Analytics.Total'),
                formatter: () => {
                  const rawValue = formatNumber(totalUsers)
                  return `${rawValue} user`
                },
              },
            },
            barLabels: {
              enabled: true,
              useSeriesColors: true,
              offsetX: -18,
              fontSize: '12px',
              formatter: function (seriesName, opts) {
                const idx = opts.seriesIndex
                const periodKeys = Object.keys(
                  InfinityEnums.UsersActivityDurations,
                )
                const key = periodKeys[idx]
                return `${periodDisplay[key]}`
              },
            },
          },
          total: {
            show: true,
            label: formatNumber(totalUsers),
            formatter: () => '',
          },
        },
        labels,
        colors: [
          Colors.Analytics.RadialBar.LastYear,
          Colors.Analytics.RadialBar.ThisYear,
          Colors.Analytics.RadialBar.Last6Months,
          Colors.Analytics.RadialBar.Last3Months,
          Colors.Analytics.RadialBar.LastMonth,
          Colors.Analytics.RadialBar.LastWeek,
        ],

        fill: {
          type: 'solid',
          opacity: 1,
        },
        stroke: {
          lineCap: 'round',
          width: 2,
        },
      },
    }
  }, [usersActivity, t])
  return (
    <div style={{ backgroundColor: Colors.white }} className={Styles.Card}>
      {fetchUsersActivityRequestState === RequestState.LOADING ? (
        <div className={Styles.ChartSkeleton}>
          <IFSkeleton width={'100%'} height={'100%'} variant="rectangular" />
        </div>
      ) : (
        <div className={Styles.Content}>
          <ChartComponent
            type="radialBar"
            options={options}
            series={series}
            height={'100%'}
            width={'100%'}
            className={Styles.Chart}
          />
        </div>
      )}
    </div>
  )
}

const mapStateToProps = (state) => ({
  usersActivity: AnalyticsSelectors.getUsersActivity(state),
  fetchUsersActivityRequestState:
    AnalyticsSelectors.getUsersActivityRequestState(state),
})

function mapDispatchToProps(dispatch) {
  return {
    fetchUsersActivity: (filter) =>
      dispatch(AnalyticsActions.fetchUsersActivity(filter)),
  }
}

UsersActivityCard.propTypes = {
  fetchUsersActivity: PropTypes.func,
  fetchUsersActivityRequestState: PropTypes.number,
  usersActivity: PropTypes.object,
  filter: PropTypes.object,
}

export default connect(mapStateToProps, mapDispatchToProps)(UsersActivityCard)
